import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Linkedin, Instagram, Twitter, ArrowRight } from 'lucide-react';
import { company, services, LOGO_URL } from '../mock';
import { Button } from './ui/button';
import { Input } from './ui/input';

export default function Footer() {
  const gc = services.filter(s => s.category === 'General Contractor');
  const sub = services.filter(s => s.category === 'Sub Contractor').slice(0, 6);

  return (
    <footer className="bg-[#06111F] text-slate-300">
      {/* CTA strip */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
              Get a free estimate <span className="text-amber-400">in 24–48 hours.</span>
            </h3>
            <p className="mt-3 text-slate-400 max-w-lg">
              Send us your drawings — a senior estimator will reply with scope confirmation, lead time and a fixed fee within 4 business hours.
            </p>
          </div>
          <form className="flex flex-col sm:flex-row gap-3" onSubmit={(e) => e.preventDefault()}>
            <Input
              type="email"
              placeholder="you@company.com"
              className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-12 rounded-xl"
            />
            <Button className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-12 px-6 rounded-xl whitespace-nowrap">
              Request Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <Link to="/" className="block">
            <img
              src={LOGO_URL}
              alt="Core Estimation LLC"
              className="h-32 w-auto object-contain drop-shadow-[0_4px_14px_rgba(201,162,74,0.18)]"
            />
          </Link>
          <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-sm">
            Core Estimation LLC delivers ASPE-aligned construction cost estimating, material takeoff and quantity
            takeoff services for contractors, developers and design firms across the United States.
          </p>
          <div className="mt-6 space-y-3 text-sm">
            <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-3 hover:text-amber-400 transition">
              <Phone className="w-4 h-4 text-amber-400" /> {company.phone}
            </a>
            <a href={`mailto:${company.email}`} className="flex items-center gap-3 hover:text-amber-400 transition">
              <Mail className="w-4 h-4 text-amber-400" /> {company.email}
            </a>
          </div>
          <div className="mt-6 flex gap-3">
            {[Facebook, Linkedin, Instagram, Twitter].map((Icon, i) => (
              <a key={i} href="#" className="w-10 h-10 rounded-full bg-white/5 hover:bg-amber-500 hover:text-[#0B1B33] flex items-center justify-center transition">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">General Contractor</h4>
          <ul className="space-y-3 text-sm">
            {gc.map(s => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="hover:text-amber-400 transition">{s.title}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">Subcontractors</h4>
          <ul className="space-y-3 text-sm">
            {sub.map(s => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="hover:text-amber-400 transition">{s.title}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">Our Offices</h4>
          <ul className="space-y-4 text-sm">
            {company.addresses.map(a => (
              <li key={a.label} className="flex gap-2">
                <MapPin className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-white text-xs font-semibold uppercase tracking-wider mb-1">{a.label}</div>
                  <div className="text-slate-400">{a.value}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between text-xs text-slate-500 gap-3">
          <div>© {new Date().getFullYear()} Core Estimation LLC. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="#" className="hover:text-amber-400 transition">Privacy Policy</Link>
            <Link to="#" className="hover:text-amber-400 transition">Terms of Service</Link>
            <Link to="#" className="hover:text-amber-400 transition">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
